import { styled } from "goober";
import { type ReactNode, useRef } from "react";
import { CircleMesh } from "../CircleMesh";
import { PressButton } from "../PressButton";
import { Icon } from "../../components/Icon";
import { Text } from "../../components/Text";

const MeshBackground = styled("div")`
  position: fixed;
  inset: 0;
  z-index: -1;
`;

const Container = styled("div")`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  align-items: center;
  text-align: center;
  justify-content: center;
  gap: 8px;
  position: relative;
  isolation: isolate;
  overflow-y: auto;
  padding: 0 16px 48px;
`;

const Inner = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 32px;
  align-items: center;
  width: 100%;
`;

const Header = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  max-width: 300px;
`;

const Footer = styled("div")`
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 32px;
  display: flex;
  justify-content: center;
`;

const LegalLink = styled("a")`
  color: inherit;
  text-decoration-color: var(--border-alpha);
  text-underline-offset: 2px;

  &:hover {
    color: var(--text-primary);
  }
`;

function LegalNotice() {
  return (
    <Text variant="text-small" color="var(--text-secondary)">
      By accessing or using the services, you agree to the terms set forth at{" "}
      <LegalLink
        href="https://tines.com/legal"
        target="_blank"
        rel="noopener noreferrer"
      >
        tines.com/legal
      </LegalLink>
      .
    </Text>
  );
}

export const SignInButton = styled(PressButton)`
  padding: 12px 20px;
  border: none;
  border-radius: 9999px;
  color: var(--text-primary);
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-width: 240px;
  font-family: inherit;

  &[data-muted="true"] > * {
    opacity: 0.5;
  }

  &[data-muted="true"]:hover > * {
    opacity: 1;
  }

  &:hover {
    background: var(--surface-hover);
  }

  &:active {
    opacity: 0.8;
  }
`;

export function SignInLayout({
  children,
  title = "Welcome to 3B",
  subtitle,
  anchorTop = false,
  hideFooter = false,
}: {
  children: ReactNode;
  title?: string;
  subtitle?: ReactNode;
  anchorTop?: boolean;
  hideFooter?: boolean;
}) {
  // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
  const randomSeed = useRef(Math.random().toString());
  return (
    <Container
      style={
        anchorTop
          ? {
              justifyContent: "flex-start",
              paddingTop: "max(24px, calc(50vh - 260px))",
            }
          : undefined
      }
    >
      <MeshBackground>
        <CircleMesh
          // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
          seed={randomSeed.current}
          targetCircleSize={115}
          mutingIntensity={1}
        />
      </MeshBackground>
      <Inner>
        <Icon name="tines-mark" style={{ width: 28, height: 28 }} />
        <Header>
          <Text
            variant="display-md"
            style={{ fontSize: "22px", lineHeight: "28px" }}
          >
            {title}
          </Text>
          {subtitle && (
            <Text
              variant="text-regular"
              color="var(--text-secondary)"
              style={{ fontSize: "13px", lineHeight: "18px" }}
            >
              {subtitle}
            </Text>
          )}
        </Header>
        {children}
      </Inner>
      {!hideFooter && (
        <Footer>
          <LegalNotice />
        </Footer>
      )}
    </Container>
  );
}
